import { type Idea } from './ProductCard';
import { fetchCataloguePartners } from './catalogueApi';

// Cle de semaine : la semaine "bascule" le lundi a 4h (heure locale). Sert de
// graine pour une selection aleatoire stable sur 7 jours (renouvelee chaque
// lundi 4h). Implementation locale, sans dependance serveur.
export function currentWeekKey(now: Date = new Date()): string {
  const shifted = new Date(now.getTime() - 4 * 60 * 60 * 1000); // recule de 4h
  const dayFromMonday = (shifted.getDay() + 6) % 7; // 0 = lundi
  const monday = new Date(shifted.getFullYear(), shifted.getMonth(), shifted.getDate() - dayFromMonday);
  return `${monday.getFullYear()}-${monday.getMonth() + 1}-${monday.getDate()}`;
}

// Props de pastille pour les grilles de ProductCard. On NE met PAS de pastille
// marque pour les produits partenaires/affilies : le badge sponsor "Partenaire"
// (coin haut-droit) + le nom de marque en bas de carte suffisent, eviter le
// double badge redondant. Seul l'item importe par la communaute garde sa pastille.
export function ideaCardProps(idea: Idea): { pillLabel: string | null; pillVariant: 'partner' | 'spotted' } {
  if (idea.source === 'USER_IMPORTED') return { pillLabel: 'Repéré ✦', pillVariant: 'spotted' };
  return { pillLabel: null, pillVariant: 'partner' };
}

// ─── Recherche du catalogue : suggestions et zero resultat ───────────────────
// Client de GET /api/catalogue/suggestions (api/src/controllers/catalogue.controller.ts).

/** Suggestions des 2 lettres tapees. */
export const SUGGEST_MIN_CHARS = 2;

/** Produit tel que renvoye par /api/catalogue (memes champs que ApiProduct). */
export interface SearchProduct {
  id: string;
  title: string;
  imageUrl: string;
  priceCents: number | null;
  currency: string;
  productUrl: string;
  partnerKey: string;
  sponsored?: boolean;
  partnerSlug?: string | null;
  partnerProductId?: string | null;
}

export interface CatalogueSuggestions {
  query: string;
  /** Suggestions de saisie tirees des titres (« doudou lapin »). */
  terms: Array<{ label: string; count: number }>;
  /** Marques dont le nom commence par la saisie. */
  marques: Array<{ key: string; name: string; count: number }>;
  /** Les 2 premiers resultats de `productsFor`. */
  products: SearchProduct[];
  totalCount: number;
  /** Recherche dont viennent products et totalCount (la saisie, ou la 1re suggestion). */
  productsFor: string | null;
  /** Saisie corrigee sur les mots du catalogue, avec son nombre d'idees. */
  didYouMean: { q: string; totalCount: number } | null;
  /** Zero resultat pour la saisie : pistes reelles. */
  fallback: null | {
    related: SearchProduct[];
    relatedCount: number;
    rayons: Array<{ key: string; label: string; count: number }>;
    coupsDeCoeur: SearchProduct[];
  };
}

/** Saisie assez longue pour chercher (espaces et ponctuation ignores). */
export function isSearchable(q: string): boolean {
  return q.replace(/[^\p{L}\p{N}]+/gu, '').length >= SUGGEST_MIN_CHARS;
}

function isSuggestions(x: unknown): x is CatalogueSuggestions {
  const o = x as Partial<CatalogueSuggestions> | null;
  return !!o && Array.isArray(o.terms) && Array.isArray(o.marques) && Array.isArray(o.products) && typeof o.totalCount === 'number';
}

let partnerNamesPromise: Promise<Record<string, string>> | null = null;

/** Noms des marques des cartes de la recherche, lus une seule fois par page. */
export function loadSearchPartnerNames(): Promise<Record<string, string>> {
  partnerNamesPromise ??= fetchCataloguePartners();
  return partnerNamesPromise;
}

/** Pour les tests : relit les noms des marques au prochain affichage. */
export function resetSearchPartnerNames(): void {
  partnerNamesPromise = null;
}

/** Adresse de GET /api/catalogue/suggestions : la saisie et les filtres en cours de la page (sans tri ni page). */
export function suggestionsUrl(q: string, filterParams?: string): string {
  const params = new URLSearchParams(filterParams ?? '');
  ['q', 'sort', 'page', 'pageSize'].forEach(k => params.delete(k));
  const rest = params.toString();
  return `/api/catalogue/suggestions?q=${encodeURIComponent(q.trim())}${rest ? `&${rest}` : ''}`;
}

/**
 * GET /api/catalogue/suggestions?q=[&filtres] ; null si la saisie est trop courte
 * ou en cas d'echec. `filterParams` : filtres de la page (memes parametres que
 * /api/catalogue), pour que « Voir les N » donne le chiffre de la liste.
 */
export async function fetchCatalogueSuggestions(q: string, signal?: AbortSignal, filterParams?: string): Promise<CatalogueSuggestions | null> {
  if (!isSearchable(q)) return null;
  try {
    const r = await fetch(suggestionsUrl(q, filterParams), { signal });
    if (!r.ok) return null;
    const data: unknown = await r.json();
    if (!isSuggestions(data)) return null;
    return { ...data, didYouMean: data.didYouMean ?? null, fallback: data.fallback ?? null, productsFor: data.productsFor ?? null };
  } catch {
    return null;
  }
}

const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/\p{Mn}/gu, '').replace(/œ/g, 'oe');

/**
 * Coupe une suggestion entre ce qui a ete tape (en gras) et la suite :
 * « doudou » + « doudou lapin » -> [« doudou », « lapin »]. Accents, casse et
 * espaces multiples ignores ; [« », label] si la suggestion ne commence pas par la saisie.
 */
export function splitTypedPrefix(label: string, typed: string): [string, string] {
  const target = fold(typed.trim().replace(/\s+/g, ' '));
  if (!target) return ['', label];
  for (let i = 1; i <= label.length; i++) {
    const head = fold(label.slice(0, i).replace(/\s+/g, ' '));
    if (head === target) return [label.slice(0, i), label.slice(i)];
    if (!target.startsWith(head)) break;
  }
  return ['', label];
}

/** Résultats « peu nombreux » : au plus une ligne de grille sur ordinateur, trois écrans sur mobile. */
export const FEW_RESULTS = 12;

/**
 * « Vouliez-vous dire » au-dessus des résultats (UT-03) : la saisie a une
 * faute (« casquete ») mais donne quand même quelques idées (un casque) ; la
 * correction est proposée dès que les résultats sont peu nombreux ou que la
 * correction en donne davantage.
 */
export function shouldOfferCorrection(totalCount: number, correction: { q: string; totalCount: number } | null, query: string): boolean {
  if (!correction || correction.totalCount <= 0) return false;
  if (correction.q.trim().toLowerCase() === query.trim().toLowerCase()) return false;
  return totalCount <= FEW_RESULTS || correction.totalCount > totalCount;
}
